import axios from 'axios';
import { useReducer, useCallback } from 'react';

import { ServiceUrl } from '@portal/lib';
import { generateYears, GenerateMakeURL } from '@portal/lead/utils';
import { getDrilldown } from './useVehicleTaxonomy';

// Coerce year to string so Map keys are always consistent regardless of
// whether callers pass numbers (URL-parsed) or strings (picker selections).
const normalizeYear = (y) => String(y);

// Module-level caches persist across re-renders and route changes.
const taxonomyCache = new Map();  // year string → drilldown object
const pendingFetches = new Map(); // year string → in-flight Promise<void>

export const useTaxonomyFilter = () => {
  // Incrementing version used as a useMemo dep so callers re-derive options
  // once new years land in the cache.
  const [cacheVersion, bumpCache] = useReducer((n) => n + 1, 0);

  const fetchYears = useCallback(async (years) => {
    const normalizedYears = years.map(normalizeYear);
    const uncached = normalizedYears.filter((y) => !taxonomyCache.has(y));

    if (!uncached.length) return;

    await Promise.all(
      uncached.map((year) => {
        // Re-use an existing in-flight promise so concurrent callers for the
        // same year share a single network request.
        if (pendingFetches.has(year)) return pendingFetches.get(year);

        const promise = axios
          .get(
            `${ServiceUrl}/GetVehicleTaxonomyByFilter${GenerateMakeURL(year)}`,
            {
              headers: {
                'Client-Name': 'dealer-portal',
              },
            }
          )
          .then((response) => {
            taxonomyCache.set(year, getDrilldown(response.data?.data?.year_list));
          })
          .catch((e) => {
            console.error('Taxonomy fetch error for year', year, e);
          })
          .finally(() => {
            pendingFetches.delete(year);
          });

        pendingFetches.set(year, promise);
        return promise;
      })
    );

    const anySucceeded = uncached.some((y) => taxonomyCache.has(y));
    if (anySucceeded) {
      bumpCache();
    }
  }, []);

  const yearOptions = generateYears(1990).map(({ value }) => value);

  const getMakeOptions = (years) => {
    const makes = new Set();
    years.map(normalizeYear).forEach((y) => {
      const d = taxonomyCache.get(y);
      if (d) Object.keys(d).forEach((m) => makes.add(m));
    });
    return [...makes].sort();
  };

  const getModelOptions = (years, makes) => {
    const models = new Set();
    years.map(normalizeYear).forEach((y) => {
      const d = taxonomyCache.get(y);
      if (!d) return;
      makes.forEach((make) => {
        if (d?.[make]) Object.keys(d[make]).forEach((m) => models.add(m));
      });
    });
    return [...models].sort();
  };

  const getStyleOptions = (years, makes, models) => {
    const styles = new Set();
    years.map(normalizeYear).forEach((y) => {
      const d = taxonomyCache.get(y);
      if (!d) return;
      makes.forEach((make) => {
        models.forEach((model) => {
          if (d?.[make]?.[model])
            Object.keys(d[make][model]).forEach((s) => styles.add(s));
        });
      });
    });
    return [...styles].sort();
  };

  return {
    cacheVersion,
    fetchYears,
    getMakeOptions,
    getModelOptions,
    getStyleOptions,
    yearOptions,
  };
};
